import React, { useState, useEffect, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { RefreshCw, Download, Activity, BarChart3, AlertCircle, Zap } from 'lucide-react';
import PageLayout from '../../components/ui/PageLayout';



const AutomationMetricsPanel = React.lazy(() => import('./components/AutomationMetricsPanel'));
const ServiceHealthMonitor = React.lazy(() => import('./components/ServiceHealthMonitor'));
const PerformanceTrendsChart = React.lazy(() => import('./components/PerformanceTrendsChart'));
const ErrorTrackingPanel = React.lazy(() => import('./components/ErrorTrackingPanel'));
const DataFlowVisualization = React.lazy(() => import('./components/DataFlowVisualization'));
const WebhookMonitor = React.lazy(() => import('./components/WebhookMonitor'));
const OptimizationSuggestions = React.lazy(() => import('./components/OptimizationSuggestions'));

const SectionLoader = () => (
  <div className="bg-white border border-gray-200 rounded-xl p-8 flex items-center justify-center">
    <div className="flex items-center gap-3 text-gray-500">
      <RefreshCw className="w-5 h-5 animate-spin" />
      <span className="text-sm">Loading...</span>
    </div>
  </div>
);

class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(`Section error in ${this.props?.name}:`, error, info);
  }

  render() {
    if (this.state?.hasError) {
      return (
        <div className="bg-white border border-red-200 rounded-xl p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">Failed to load {this.props?.name}</p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="text-xs text-red-500 underline mt-1"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

const SafeSection = ({ name, children }) => (
  <SectionErrorBoundary name={name}>
    <Suspense fallback={<SectionLoader />}>
      {children}
    </Suspense>
  </SectionErrorBoundary>
);

const StatBadge = ({ icon: IconComponent, label, value, color }) => (
  <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${color}`}>
    <IconComponent className="w-4 h-4" />
    <div>
      <p className="text-xs opacity-70">{label}</p>
      <p className="text-sm font-bold">{value}</p>
    </div>
  </div>
);

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'performance', label: 'Performance', icon: BarChart3 },
  { id: 'errors', label: 'Errors & Alerts', icon: AlertCircle },
  { id: 'webhooks', label: 'Webhooks', icon: Zap },
];

const IntegrationAnalyticsMonitoring = () => {
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const handleRefresh = async () => {
    setRefreshing(true);
    await new Promise(r => setTimeout(r, 1200));
    setLastRefreshed(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    const interval = setInterval(() => setLastRefreshed(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleExport = () => {
    const report = {
      generatedAt: new Date()?.toISOString(),
      summary: {
        overallHealth: 'Good',
        automationSuccessRate: '97.4%',
        activeIntegrations: 6,
        totalWebhooks: 12,
        errorRate: '0.8%',
        avgResponseTime: '245ms'
      },
      services: [
        { name: 'GoHighLevel', status: 'connected', uptime: '99.9%' },
        { name: 'Twilio SMS', status: 'warning', uptime: '97.1%' },
        { name: 'Resend Email', status: 'connected', uptime: '99.8%' },
        { name: 'Supabase DB', status: 'connected', uptime: '100%' },
        { name: 'AI Dialer', status: 'connected', uptime: '98.7%' },
        { name: 'Stripe Payments', status: 'connected', uptime: '99.9%' },
      ]
    };
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `integration-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Integration Analytics & Monitoring - LeadGen Pro</title>
      </Helmet>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Integration Analytics & Monitoring</h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {lastRefreshed?.toLocaleTimeString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export Report
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="flex flex-wrap gap-3">
          <StatBadge icon={Activity} label="Overall Health" value="Good" color="bg-green-50 border-green-200 text-green-700" />
          <StatBadge icon={BarChart3} label="Success Rate" value="97.4%" color="bg-blue-50 border-blue-200 text-blue-700" />
          <StatBadge icon={Zap} label="Active Integrations" value="6/7" color="bg-purple-50 border-purple-200 text-purple-700" />
          <StatBadge icon={AlertCircle} label="Error Rate" value="0.8%" color="bg-yellow-50 border-yellow-200 text-yellow-700" />
        </div>
      </div>
      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6 overflow-x-auto">
        {TABS?.map(tab => {
          const TabIcon = tab?.icon;
          return (
            <button
              key={tab?.id}
              onClick={() => setActiveSection(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeSection === tab?.id
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab?.label}
            </button>
          );
        })}
      </div>
      {/* Tab Content */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          <SafeSection name="Service Health">
            <ServiceHealthMonitor />
          </SafeSection>
          <SafeSection name="Automation Metrics">
            <AutomationMetricsPanel />
          </SafeSection>
          <SafeSection name="Data Flow">
            <DataFlowVisualization />
          </SafeSection>
        </div>
      )}
      {activeSection === 'performance' && (
        <div className="space-y-6">
          <SafeSection name="Performance Trends">
            <PerformanceTrendsChart />
          </SafeSection>
          <SafeSection name="Optimization Suggestions">
            <OptimizationSuggestions />
          </SafeSection>
        </div>
      )}
      {activeSection === 'errors' && (
        <div className="space-y-6">
          <SafeSection name="Error Tracking">
            <ErrorTrackingPanel />
          </SafeSection>
        </div>
      )}
      {activeSection === 'webhooks' && (
        <div className="space-y-6">
          <SafeSection name="Webhook Monitor">
            <WebhookMonitor />
          </SafeSection>
        </div>
      )}
    </PageLayout>
  );
};

export default IntegrationAnalyticsMonitoring;
